import React, { useEffect, useState } from 'react';
import { appConfig } from './config';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import LoginScreen from './components/LoginScreen';
import Dashboard from './components/Dashboard';
import MyCourses from './components/MyCourses';
import Profile from './components/Profile';
import Challenges from './components/Challenges';
import Favourites from './components/Favourites';
import ErrorPage from './components/ErrorPage';
import NotFoundPage from './components/NotFoundPage';
import PrivacyPage from './components/PrivacyPage';
import HelpPage from './components/HelpPage';
import CourseDetail from './components/CourseDetail';
import ElementPage from './components/ElementPage';
import ElementFeed from './components/ElementFeed';
import { AuthProvider, useAuth } from './context/AuthContext';
import { LanguageProvider, useLanguage } from './context/LanguageContext';
import ElementCreator from './components/ElementCreator';
import CourseBuilder from './components/CourseBuilder';
import QuizBuilder from './components/QuizBuilder'; 
import AdminPanel from './components/AdminPanel';

import './App.css';

// Compute router basename consistently for dev/prod
function getBasename() {
  try {
    const url = new URL(appConfig.baseUrl);
    const fromConfig = url.pathname.endsWith('/') ? url.pathname.slice(0, -1) : url.pathname;
    if (fromConfig && fromConfig !== '/') return fromConfig;
  } catch (_) {}

  try {
    const path = window.location?.pathname || '';
    const common = ['/hejvi'];
    const detected = common.find(p => p && p !== '/' && path.startsWith(p));
    if (detected) return detected;
  } catch (_) {}

  return '';
}

// =============================================================================
// LicenceExpiredOverlay
//
// Zwei Modi je nach Schweregrad:
//
//   Grace Period (isGracePeriod = true):
//     → Orange Banner oben — App bleibt benutzbar (nur Lesen)
//     → Dismissbar für die aktuelle Session
//     → Banner erscheint erneut bei nächster blockierter Schreiboperation
//
//   Fully Expired (isGracePeriod = false):
//     → Dunkles Vollbild-Overlay — App vollständig gesperrt
//     → Nicht dismissbar, nur Ausloggen möglich
//
// Erkennung: Grace Period → Backend-Meldung enthält "Lesezugriff"
//            Fully Expired → Meldung enthält "Kulanzfrist" + "abgelaufen"
// =============================================================================
function LicenceExpiredOverlay({ message, isGracePeriod, onLogout, onDismiss }) {
  if (isGracePeriod) {
    return (
      <div
        style={{
          position:   'fixed',
          top:        0,
          left:       0,
          right:      0,
          zIndex:     9998,
          background: '#fffbeb',
          borderBottom: '2px solid #f59e0b',
          padding:    '10px 16px',
          display:    'flex',
          alignItems: 'center',
          gap:        12,
          boxShadow:  '0 2px 8px rgba(245,158,11,0.2)',
        }}
      >
        <span style={{ fontSize: 20, flexShrink: 0 }}>⚠️</span>
        <span style={{ flex: 1, fontSize: 13, color: '#92400e', lineHeight: 1.4 }}>
          <strong style={{ display: 'block', marginBottom: 2 }}>Lizenz abgelaufen — Kulanzfrist aktiv</strong>
          {message}
        </span>
        <button
          onClick={onLogout}
          style={{
            background: 'transparent',
            border:     '1px solid #d97706',
            color:      '#92400e',
            borderRadius: 6,
            padding:    '5px 12px',
            fontSize:   12,
            cursor:     'pointer',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}
        >
          Ausloggen
        </button>
        <button
          onClick={onDismiss}
          title="Banner schließen (erscheint erneut bei nächster Schreiboperation)"
          style={{
            background: '#f59e0b',
            border:     'none',
            color:      '#fff',
            borderRadius: 6,
            padding:    '5px 12px',
            fontSize:   12,
            cursor:     'pointer',
            whiteSpace: 'nowrap',
            flexShrink: 0,
            fontWeight: 600,
          }}
        >
          Verstanden ×
        </button>
      </div>
    );
  }

  // Fully expired — blockierendes Overlay
  return (
    <div
      style={{
        position:       'fixed',
        inset:          0,
        zIndex:         9999,
        background:     'rgba(15, 23, 42, 0.93)',
        display:        'flex',
        alignItems:     'center',
        justifyContent: 'center',
        padding:        24,
      }}
    >
      <div
        style={{
          background:   '#ffffff',
          borderRadius: 20,
          padding:      '40px 32px',
          maxWidth:     420,
          width:        '100%',
          textAlign:    'center',
          boxShadow:    '0 25px 50px rgba(0,0,0,0.4)',
        }}
      >
        <div style={{ fontSize: 56, marginBottom: 16 }}>🔒</div>
        <h2 style={{ margin: '0 0 12px', fontSize: 22, color: '#0f172a', fontWeight: 700 }}>
          Lizenz abgelaufen
        </h2>
        <p style={{ color: '#64748b', fontSize: 14, lineHeight: 1.6, margin: '0 0 8px' }}>
          {message}
        </p>
        <p style={{ color: '#94a3b8', fontSize: 13, margin: '0 0 28px' }}>
          Bitte kontaktieren Sie Ihren Administrator oder den HejVi-Support.
        </p>
        <button
          onClick={onLogout}
          style={{
            background:   '#FF6407',
            color:        '#fff',
            border:       'none',
            borderRadius: 10,
            padding:      '12px 32px',
            fontSize:     15,
            fontWeight:   600,
            cursor:       'pointer',
            width:        '100%',
          }}
        >
          Ausloggen
        </button>
      </div>
    </div>
  );
}

// Protected Route component with redirect support
function ProtectedRoute({ children, redirectTo = null }) {
  const { user, loading } = useAuth();
  const { t } = useLanguage();
  const location = window.location;

  if (loading) {
    return (
      <div className="mobile-container">
        <div className="loading-screen">
          <div className="spinner"></div>
          <p>{t('loading') || 'Loading Hejvi...'}</p>
        </div>
      </div>
    );
  }

  if (!user) {
    const basename = getBasename();
    let currentPath = location.pathname + location.search;

    if (basename && currentPath.startsWith(basename)) {
      currentPath = currentPath.substring(basename.length) || '/';
    }

    if (currentPath !== '/login') {
      localStorage.setItem('hejvi_redirect_after_login', currentPath);
    }
    return <Navigate to="/login" replace />;
  }

  return <div className="mobile-container">{children}</div>;
}

// Public Route component (for login) with PIN support
function PublicRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="mobile-container">
        <div className="loading-screen">
          <div className="spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (user) {
    let redirectPath = localStorage.getItem('hejvi_redirect_after_login');
    if (redirectPath) {
      const basename = getBasename();
      if (basename && redirectPath.startsWith(basename)) {
        redirectPath = redirectPath.substring(basename.length) || '/';
      }
      localStorage.removeItem('hejvi_redirect_after_login');
      try {
        new URL(redirectPath, window.location.origin);
        return <Navigate to={redirectPath} replace />;
      } catch (_) {
        return <Navigate to="/" replace />;
      }
    }
    return <Navigate to="/" replace />;
  }

  return <div className="mobile-container">{children}</div>;
}

function AppContent() {
  const {
    licenceExpired,
    licenceExpiredMessage,
    clearLicenceExpired,
    logout,
  } = useAuth();

  // Lokal: Banner-Dismiss für Grace-Period-Session
  // clearLicenceExpired() wird NICHT aufgerufen — Status bleibt true,
  // Banner erscheint bei nächster blockierter Schreiboperation wieder.
  const [bannerDismissed, setBannerDismissed] = useState(false);

  // Banner-Dismiss bei neuer Fehlermeldung zurücksetzen
  useEffect(() => {
    if (licenceExpired) setBannerDismissed(false);
  }, [licenceExpired, licenceExpiredMessage]);

  // Erkennung: Grace Period vs. hart abgelaufen
  // Grace Period: Meldung enthält "Lesezugriff" (Backend: assertLicenceActive(true) in Grace Period)
  // Fully Expired: Meldung enthält "Kulanzfrist" + "abgelaufen"
  const isGracePeriod = licenceExpired && licenceExpiredMessage.includes('Lesezugriff');
  const isFullyExpired = licenceExpired && !isGracePeriod;

  const handleLogout = async () => {
    clearLicenceExpired();
    await logout();
  };

  // Navigation tracking
  useEffect(() => {
    const handleNavigation = () => {};
    window.addEventListener('popstate', handleNavigation);
    return () => window.removeEventListener('popstate', handleNavigation);
  }, []);

  return (
    <>
      {/* Grace Period: Banner über der App — Inhalt bleibt nutzbar */}
      {isGracePeriod && !bannerDismissed && (
        <LicenceExpiredOverlay
          message={licenceExpiredMessage}
          isGracePeriod={true}
          onLogout={handleLogout}
          onDismiss={() => setBannerDismissed(true)}
        />
      )}

      {/* Fully Expired: blockierendes Vollbild-Overlay */}
      {isFullyExpired && (
        <LicenceExpiredOverlay
          message={licenceExpiredMessage}
          isGracePeriod={false}
          onLogout={handleLogout}
          onDismiss={null}
        />
      )}

      {/* App-Inhalt: bei fully expired per pointer-events deaktivieren,
          damit kein Klick durch das Overlay durchkommt */}
      <div style={isFullyExpired ? { pointerEvents: 'none', userSelect: 'none' } : {}}>
        <Routes>
          {/* Public Routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <LoginScreen />
              </PublicRoute>
            }
          />

          {/* Protected Routes */}
          <Route path="/" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/my-courses" element={<ProtectedRoute><MyCourses /></ProtectedRoute>} />
          <Route path="/challenges" element={<ProtectedRoute><Challenges /></ProtectedRoute>} />
          <Route path="/favourites" element={<ProtectedRoute><Favourites /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/video/:videoId" element={<ProtectedRoute><ErrorPage /></ProtectedRoute>} />
          <Route path="/course/:courseId" element={<ProtectedRoute><CourseDetail /></ProtectedRoute>} />
          <Route path="/element/:hashId" element={<ProtectedRoute><ElementPage /></ProtectedRoute>} />
          <Route path="/feed/:hashId" element={<ProtectedRoute><ElementFeed /></ProtectedRoute>} />
          <Route path="/element-feed/:hashId" element={<ProtectedRoute><ElementFeed /></ProtectedRoute>} />
          <Route path="/create-element" element={<ProtectedRoute><ElementCreator /></ProtectedRoute>} />
          <Route path="/create-course" element={<ProtectedRoute><CourseBuilder /></ProtectedRoute>} />
          <Route path="/collection/:hashId" element={<ProtectedRoute><CourseDetail /></ProtectedRoute>} />
          <Route path="/create-quiz" element={<ProtectedRoute><QuizBuilder /></ProtectedRoute>} />
          <Route path="/error" element={<ProtectedRoute><ErrorPage /></ProtectedRoute>} />

          {/* Static pages */}
          <Route path="/profile/privacy" element={<ProtectedRoute><PrivacyPage /></ProtectedRoute>} />
          <Route path="/profile/help" element={<ProtectedRoute><HelpPage /></ProtectedRoute>} />

          {/* Admin */}
          <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />

          {/* Catch all */}
          <Route path="*" element={<ProtectedRoute><NotFoundPage /></ProtectedRoute>} />
        </Routes>
      </div>
    </>
  );
}

function App() {
  return (
    <LanguageProvider>
      <AuthProvider>
        <Router
          basename={getBasename()}
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
          window={window}
        >
          <AppContent />
        </Router>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;
